"use client";

import React, { useEffect, useLayoutEffect, useState } from "react";
import { useSelector } from "react-redux";
import Index from "../Header/Index";
import image from "../../assets/Images/finalLogo.png";
import image2 from "../../assets/Images/newLogo.png";
import TodoDrawer from "../todo-list/TodoDrawer";
import { useLocation, useNavigate } from "react-router-dom";
import { buildUrl } from "../common/navigation";
import { isCOO, isPresident } from "../common/privileges";
import NotificationsDrawer from "../notifications/NotificationsDrawer";
import { useNotifications } from "../hooks/useNotifications";

export const Header = () => {
  const { fetchNotificationsIfStale } = useNotifications();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data } = useSelector((state) => state?.data?.userInfo?.data);
  const isPresidentUser = isPresident(data?.EmpId);
  const isCooUser = isCOO(data?.EmpId);
  const isProsoftUser = data?.RoleId === 14;

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024,
  );

  // President's logo click lands on the same place as after login — BMR
  // Engineer Tickets' Engineer Summary tab — since Dashboard (and the
  // Client/Software/Support menus) are hidden for this user anyway; see
  // the matching `postLoginPath` logic in App.js.
  // Service ID: #3867 — COO logo navigation opens the biller report.
  const handleLogoClick = () => {
    navigate(
      isCooUser
        ? buildUrl("BillerSummary", { tab: "Biller Summary" })
        : isPresidentUser || isProsoftUser
          ? buildUrl("BMRSoftwareTickets", { tab: "EngineerSummary" })
          : buildUrl("Dashboard"),
    );
  };

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;

    const updateWidth = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  // Rendered once by AppLayout and kept mounted across route changes, so this
  // runs on the initial mount rather than on every navigation. New
  // notifications still arrive live over SignalR (see useListenMessages, which
  // this header also keeps mounted); the freshness guard remains as a backstop
  // for the initial load.
  useLayoutEffect(() => {
    fetchNotificationsIfStale();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The header used to unmount on every route change, which implicitly closed
  // both drawers. Now that it survives navigation, close them explicitly so
  // changing screens behaves exactly as it did before.
  useEffect(() => {
    setIsDrawerOpen(false);
    setIsNotificationsOpen(false);
  }, [pathname]);

  return (
    <div className="app-header w-full shadow-sm">
      <div className="flex h-16 sm:h-18">
        {/* Logo Section - Fixed width */}
        <div className="flex items-center justify-center bg-sideNavbg px-4 w-20 sm:w-32 md:w-48 lg:w-52 xxl:w-64 transition-all duration-300">
          <div
            className="flex cursor-pointer hover:scale-105 transition-transform duration-200"
            onClick={handleLogoClick}
          >
            <img
              src={windowWidth < 1300 ? image : image2}
              alt="Support Portal"
              className="h-8 sm:h-10 md:h-12 w-auto object-contain filter drop-shadow-sm"
            />
          </div>
        </div>

        {/* Navigation Section - Takes remaining space */}
        <div className="pc-shell-nav flex-1 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-[#19232e] dark:to-[#222f3d] border-l border-gray-200">
          <Index
            setIsDrawerOpen={setIsDrawerOpen}
            setIsNotificationsOpen={setIsNotificationsOpen}
          />
          <TodoDrawer isOpen={isDrawerOpen} setIsOpen={setIsDrawerOpen} />
          <NotificationsDrawer
            isOpen={isNotificationsOpen}
            setIsOpen={setIsNotificationsOpen}
          />
        </div>
      </div>
    </div>
  );
};
